import React, { useState } from "react";
import { toast } from "react-toastify";
import { saveGiftMemo, setGiftSecretWord, isOk, messageOf } from "../../../services/gift";

// What only the purchaser sees and controls on their gift: the secret word
// VerySoul quotes in payment requests, and a private memo to themselves.
// onChanged receives the refreshed gift.
export default function GiftOwnerPanel({ gift, onChanged }) {
  const [word, setWord] = useState("");
  const [changingWord, setChangingWord] = useState(false);
  const [memo, setMemo] = useState(gift.private_memo || "");
  const [busy, setBusy] = useState("");

  const run = async (key, request, fallback) => {
    setBusy(key);
    const res = await request;
    setBusy("");
    if (isOk(res)) {
      toast.success(messageOf(res, "Saved."));
      onChanged(res.data.data);
      return true;
    }
    toast.error(messageOf(res, fallback));
    return false;
  };

  const saveWord = async () => {
    if (await run("word", setGiftSecretWord(gift.gift_id, word.trim()), "Your secret word could not be saved.")) {
      setWord("");
      setChangingWord(false);
    }
  };

  const showWordForm = !gift.has_secret_word || changingWord;

  return (
    <div className="vsr-card vsr-card-panel vs-owner no-print">
      <div className="vs-owner-block">
        <h2 className="vsr-designer-title">Secret word</h2>
        {!gift.has_secret_word && (
          <p className="vs-owner-alert">
            This gift has no secret word yet. Add one so you can tell a real VerySoul payment request from a fake.
          </p>
        )}
        <p className="vsr-muted">
          When someone uses this gift, VerySoul's payment request email shows your secret word. If it isn't there, don't pay.
        </p>
        {showWordForm ? (
          <div className="vs-owner-row">
            <input
              className="vsr-input"
              type="text"
              maxLength={60}
              value={word}
              onChange={(e) => setWord(e.target.value)}
              placeholder="A word or phrase only you would pick (3–60 characters)"
              aria-label="Secret word"
            />
            <button type="button" className="vsr-btn vsr-btn-sm" disabled={busy === "word" || word.trim().length < 3} onClick={saveWord}>
              {busy === "word" ? "Saving…" : "Save word"}
            </button>
            {changingWord && (
              <button type="button" className="vsr-link" onClick={() => setChangingWord(false)}>
                Cancel
              </button>
            )}
          </div>
        ) : (
          <p>
            Your secret word is set.{" "}
            <button type="button" className="vsr-link" onClick={() => setChangingWord(true)}>
              Change it
            </button>
          </p>
        )}
      </div>

      <div className="vs-owner-block">
        <h2 className="vsr-designer-title">Private memo</h2>
        <p className="vsr-muted">A note to yourself about this gift. Only you can see it. It never appears on the card or in any email.</p>
        <textarea
          className="vsr-input vs-owner-memo"
          rows={3}
          maxLength={2000}
          value={memo}
          onChange={(e) => setMemo(e.target.value)}
          placeholder="e.g. For Mum's 70th birthday, give it to her in person"
          aria-label="Private memo"
        />
        <div className="vs-owner-row">
          <button
            type="button"
            className="vsr-btn vsr-btn-sm"
            disabled={busy === "memo" || memo === (gift.private_memo || "")}
            onClick={() => run("memo", saveGiftMemo(gift.gift_id, memo), "Your memo could not be saved.")}
          >
            {busy === "memo" ? "Saving…" : "Save memo"}
          </button>
          <span className="vsr-hint">{memo.length} / 2000</span>
        </div>
      </div>
    </div>
  );
}
